const LOGO_DEV_BASE = "https://img.logo.dev";

/**
 * Normalize a link so it can be parsed by URL (e.g. add protocol if missing).
 * @param {string} link
 * @returns {string|undefined}
 */
function normalizeLink(link) {
  if (!link || typeof link !== "string") return undefined;
  const trimmed = link.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return "https://" + trimmed;
}

/**
 * Extract domain from a URL string, stripping "www." if present.
 * @param {string} link - Full URL (e.g. https://www.cnbc.com/article/...) or domain (e.g. cnbc.com)
 * @returns {string|undefined} - Hostname (e.g. cnbc.com) or undefined if parsing fails
 */
function getDomainFromLink(link) {
  const normalized = normalizeLink(link);
  if (!normalized) return undefined;
  try {
    const url = new URL(normalized);
    const hostname = url.hostname || "";
    if (!hostname) return undefined;
    return hostname.replace(/^www\./i, "");
  } catch {
    return undefined;
  }
}

/**
 * Build a Logo.dev CDN URL for a given domain.
 * @param {string} domain - e.g. "cnbc.com"
 * @param {string} token - Logo.dev publishable key (pk_...)
 * @param {number} [size=32] - Image size in pixels
 * @returns {string|undefined} - Full image URL or undefined if token/domain missing
 */
function buildLogoDevUrl(domain, token, size = 32) {
  if (!domain || !token) return undefined;
  const params = new URLSearchParams({
    token,
    size: String(size),
    format: "png",
    fallback: "monogram",
    retina: "true",
  });
  return `${LOGO_DEV_BASE}/${encodeURIComponent(domain)}?${params.toString()}`;
}

/**
 * Get Logo.dev image URL from an article (protocol SourceArticle or legacy source).
 * @param {object} article - Object with link (or url)
 * @param {string} token - Logo.dev publishable key
 * @param {number} [size=32] - Image size in pixels
 * @returns {string|undefined}
 */
export function getLogoUrlFromArticle(article, token, size = 32) {
  const link = article?.link ?? article?.url;
  const domain = getDomainFromLink(link);
  return buildLogoDevUrl(domain, token, size);
}

/**
 * Get Logo.dev image URL from a citation (side-panel shape with logo/link).
 * @param {object} citation - Object with link
 * @param {string} token - Logo.dev publishable key
 * @param {number} [size=32] - Image size in pixels
 * @returns {string|undefined}
 */
export function getLogoUrlFromCitation(citation, token, size = 32) {
  const link = citation?.link ?? citation?.url;
  const domain = getDomainFromLink(link);
  return buildLogoDevUrl(domain, token, size);
}
